import { Button, Icon, Label, SegmentedButtons, token } from "@may-i/mash-design-system";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import type { CameraMemo, CameraMemoType } from "~/entities/camera-memo/types";
import type { MemoAction } from "~/pages/camera-mapping/MemoPanel";
import { DeleteConfirmDialog } from "~/pages/resource-management/DeleteConfirmDialog";

interface InlineMemoSectionProps {
  memos: CameraMemo[];
  selectedCameraId: number | null;
  onSave: (actions: MemoAction[]) => void;
  onToggleMemoPlacement: (memo: CameraMemo) => void;
  placementLabels?: { add: string; remove: string };
  /** "이 컨텍스트(현재 도면 등)에 배치되어 있는가" 판정. 미지정 시 positionForFloorPlan 만 검사 (스냅샷 의미). */
  isPlacedOnContext?: (memo: CameraMemo) => boolean;
  /** 6차 ap: 왼쪽 메모 패널 selection 과 상단 노출 메모 동기화. selectedMemoId 가 현재 카메라 메모 중 하나면 그 메모로 currentIndex 점프 */
  selectedMemoId?: number | null;
  /** 7차 ax: 상단 화살표로 메모 이동 시 왼쪽 패널 selection 도 함께 갱신하기 위한 콜백 (역방향 동기화) */
  onMemoIdChange?: (memoId: number | null) => void;
}

export function InlineMemoSection({
  memos,
  selectedCameraId,
  onSave,
  onToggleMemoPlacement,
  placementLabels,
  isPlacedOnContext,
  selectedMemoId,
  onMemoIdChange,
}: InlineMemoSectionProps) {
  const { t: tMemo } = useTranslation("camera-mapping");
  const { t } = useTranslation("analysis-draft");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAdding, setIsAdding] = useState(false);
  const [newContent, setNewContent] = useState("");
  const [newType, setNewType] = useState<CameraMemoType>("default");

  const cameraMemos = memos
    .filter((m) => m.cameraId === selectedCameraId)
    .sort((a, b) => b.updatedAt - a.updatedAt);

  useEffect(() => {
    setCurrentIndex(0);
    setIsAdding(false);
  }, [selectedCameraId]);

  useEffect(() => {
    if (currentIndex >= cameraMemos.length && cameraMemos.length > 0) {
      setCurrentIndex(cameraMemos.length - 1);
    }
  }, [cameraMemos.length, currentIndex]);

  // 6차 ap: 외부 selectedMemoId 변경 시 — 현재 카메라 메모 중 매칭되는 메모로 currentIndex 동기화
  useEffect(() => {
    if (selectedMemoId == null) return;
    const idx = cameraMemos.findIndex((m) => m.id === selectedMemoId);
    if (idx !== -1) setCurrentIndex(idx);
  }, [selectedMemoId, cameraMemos]);

  // 위 useEffect 보정은 렌더 후에 실행되므로, 카메라 전환·메모 삭제 직후 렌더에서는 currentIndex 가 범위를 넘을 수 있음 → 표시용 인덱스를 범위 안으로 제한
  const displayIndex = Math.min(currentIndex, Math.max(cameraMemos.length - 1, 0));
  const currentMemo = cameraMemos[displayIndex];

  const handlePrev = useCallback(() => {
    setCurrentIndex((i) => {
      const next = Math.max(0, i - 1);
      if (next !== i) onMemoIdChange?.(cameraMemos[next]?.id ?? null);
      return next;
    });
  }, [cameraMemos, onMemoIdChange]);

  const handleNext = useCallback(() => {
    setCurrentIndex((i) => {
      const next = Math.min(cameraMemos.length - 1, i + 1);
      if (next !== i) onMemoIdChange?.(cameraMemos[next]?.id ?? null);
      return next;
    });
  }, [cameraMemos, onMemoIdChange]);

  const handleAdd = useCallback(() => {
    setIsAdding(true);
    setNewContent("");
    setNewType("default");
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, []);

  const handleCancel = useCallback(() => {
    setIsAdding(false);
    setNewContent("");
  }, []);

  const handleSave = useCallback(() => {
    if (!selectedCameraId || !newContent.trim()) return;
    onSave([{
      actionType: "create",
      data: {
        cameraId: selectedCameraId,
        type: newType,
        content: newContent.trim(),
        floorPlanId: null,
        positionForFloorPlan: null,
        createdAt: null,
      },
    }]);
    setIsAdding(false);
    setNewContent("");
    setCurrentIndex(0);
  }, [selectedCameraId, newContent, newType, onSave]);

  const handleChangeType = useCallback(() => {
    if (!currentMemo) return;
    const nextType: CameraMemoType = currentMemo.type === "default" ? "adjustmentOrAddition" : "default";
    onSave([{ actionType: "update", data: { ...currentMemo, type: nextType } }]);
  }, [currentMemo, onSave]);

  const [pendingDelete, setPendingDelete] = useState<CameraMemo | null>(null);

  const handleDelete = useCallback(() => {
    if (!currentMemo) return;
    // 영구 삭제는 다른 메모 삭제 경로와 동일하게 확인 다이얼로그
    setPendingDelete(currentMemo);
  }, [currentMemo]);

  const handleConfirmDelete = useCallback(() => {
    if (!pendingDelete) return;
    onSave([{ actionType: "delete", data: pendingDelete }]);
    setPendingDelete(null);
  }, [pendingDelete, onSave]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === "Escape") {
      handleCancel();
    }
  }, [handleSave, handleCancel]);

  if (!selectedCameraId) return null;

  if (isAdding) {
    return (
      <Container>
        <AddForm>
          <AddFormHeader>
            <SegmentedButtons
              size="xSmall"
              variant="outlined"
              color="primary"
              menu={[
                { value: "default", label: tMemo("memo.typeDefault") },
                { value: "adjustmentOrAddition", label: tMemo("memo.typeAdjustment") },
              ]}
              value={newType}
              onValueChange={(value) => {
                if (value) setNewType(value as CameraMemoType);
              }}
            />
          </AddFormHeader>
          <StyledTextarea
            ref={textareaRef}
            placeholder={tMemo("memo.placeholder")}
            value={newContent}
            onChange={(e) => setNewContent(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
          />
          <AddFormActions>
            <Button variant="outlined" color="primary" size="xSmall" onClick={handleCancel}>
              {tMemo("memo.cancel")}
            </Button>
            <Button variant="contained" color="primary" size="xSmall" onClick={handleSave} disabled={!newContent.trim()}>
              {tMemo("memo.save")}
            </Button>
          </AddFormActions>
        </AddForm>
      </Container>
    );
  }

  return (
    <Container>
      <Row>
        {cameraMemos.length === 0 && (
          <EmptyText>{tMemo("memo.empty")}</EmptyText>
        )}
        {cameraMemos.length > 0 && (
          <CarouselSection>
            <NavButton onClick={handlePrev} disabled={displayIndex === 0}>
              <Icon icon="ic_chevron_left" width={16} />
            </NavButton>
            <MemoTypeLabel
              $type={currentMemo.type}
              variant={currentMemo.type === "adjustmentOrAddition" ? "soft" : "filled"}
              color="default"
              size="xSmall"
            >
              {currentMemo.type === "default" ? tMemo("memo.typeDefault") : tMemo("memo.typeAdjustment")}
            </MemoTypeLabel>
            <ContentText>{currentMemo.content}</ContentText>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <NavButton as="button">
                  <Icon icon="ic_more" width={16} />
                </NavButton>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownContent sideOffset={4} align="end">
                  <DropdownItem onSelect={handleChangeType}>
                    {currentMemo.type === "default"
                      ? tMemo("memo.typeAdjustment")
                      : tMemo("memo.typeDefault")}
                  </DropdownItem>
                  <DropdownItem onSelect={() => onToggleMemoPlacement(currentMemo)}>
                    {(isPlacedOnContext?.(currentMemo) ?? (currentMemo.positionForFloorPlan != null))
                      ? (placementLabels?.remove ?? t("inlineMemo.removeFromSnapshot"))
                      : (placementLabels?.add ?? t("inlineMemo.addToSnapshot"))}
                  </DropdownItem>
                  <DropdownItem onSelect={handleDelete}>
                    {tMemo("memo.delete")}
                  </DropdownItem>
                </DropdownContent>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
            <NavButton onClick={handleNext} disabled={displayIndex === cameraMemos.length - 1}>
              <Icon icon="ic_chevron_right" width={16} />
            </NavButton>
            <PageText>{displayIndex + 1} / {cameraMemos.length}</PageText>
          </CarouselSection>
        )}
        <AddButtonWrapper>
          <Button variant="text" color="primary" size="xSmall" startIcon="ic_add" onClick={handleAdd}>
            {tMemo("memo.addMemo")}
          </Button>
        </AddButtonWrapper>
      </Row>
      <DeleteConfirmDialog
        open={pendingDelete !== null}
        title={tMemo("dialog.deleteMemoTitle")}
        name=""
        messageKey=""
        message={tMemo("dialog.deleteMemoMessage")}
        ns="camera-mapping"
        onCancel={() => setPendingDelete(null)}
        onConfirm={handleConfirmDelete}
      />
    </Container>
  );
}

// --- Styles ---

const MemoTypeLabel = styled(Label)<{ $type: CameraMemoType }>`
  ${({ $type }) =>
    $type === "adjustmentOrAddition" &&
    `--label-color: #EB6227;
     --label-background-color: rgba(235, 98, 39, 0.16);`}
`;

const Container = styled.div`
  padding: 8px 12px 8px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 28px;
`;

const CarouselSection = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
`;

const AddButtonWrapper = styled.div`
  flex-shrink: 0;
  margin-left: auto;
`;

const NavButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: ${token("colors.text.secondary")};
  border-radius: 4px;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: ${token("colors.action.hover")};
    color: ${token("colors.text.primary")};
  }

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
`;

const EmptyText = styled.span`
  flex: 1;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 13px;
  font-weight: 400;
  line-height: 28px;
  color: ${token("colors.text.secondary")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const ContentText = styled.span`
  flex: 1;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 13px;
  font-weight: 400;
  line-height: 28px;
  color: ${token("colors.text.primary")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const PageText = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 28px;
  color: ${token("colors.text.secondary")};
  white-space: nowrap;
  flex-shrink: 0;
`;

const DropdownContent = styled(DropdownMenu.Content)`
  min-width: 160px;
  background: #fff;
  border: 1px solid ${token("colors.gray.GR300")};
  border-radius: 8px;
  padding: 4px 0;
  box-shadow: ${token("shadows.dropdown")};
  z-index: 50;
`;

const DropdownItem = styled(DropdownMenu.Item)`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  cursor: pointer;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.5;
  color: ${token("colors.text.primary")};
  outline: none;

  &:hover,
  &[data-highlighted] {
    background: ${token("colors.transparent.GR600.16")};
  }
`;

const AddForm = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(143, 149, 178, 0.32);
  border-radius: 6px;
  overflow: hidden;
`;

const AddFormHeader = styled.div`
  padding: 6px 10px 0;
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  padding: 6px 10px;
  border: none;
  outline: none;
  resize: none;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.5;
  color: ${token("colors.text.primary")};
  background: transparent;

  &::placeholder {
    color: #8f95b2;
  }
`;

const AddFormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 4px;
  padding: 2px 8px 6px;
`;
